import * as React from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import styled from 'styled-components';
import AsyncSelect from 'react-select/async';

import { popupData, PopupNames } from 'core';
import { usePopupStateOperations, useInvoiceMatching } from 'core/hooks';
import {
  PaginatedStudentsDocument,
  PaginatedStudentsQuery,
  PaginatedStudentsQueryVariables,
  StudentDetailDocument,
  StudentDetailQuery,
  StudentDetailQueryVariables,
} from 'core/graphql';
import { Button } from 'atoms';
import { Colors } from 'services/ColorService';

import { PopupContainer } from '../PopupContainer';

export interface IMatchInvoicePopupData {
  invoiceId: string;
  /** Buyer email from the invoice — prefilled as the search query. */
  buyerEmail?: string;
  /**
   * Active group options for the picked student. Caller can pass them in when
   * already known; otherwise we auto-fetch from the student's agreements when
   * the user selects a student (TZ §3.4).
   */
  groupOptions?: { value: string; label: string }[];
  onMatched?: () => void;
}

interface IStudentOption {
  value: string;
  label: string;
}

/**
 * Match popup (TZ §3.4) — async student lookup with a buyer-email prefill so
 * the most common path is one Enter away. The group step is intentionally
 * optional: if it's blank the backend resolves the student's active group.
 */
export const MatchInvoicePopup = memo(() => {
  const { t } = useTranslation();
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();
  const data = popupData()[PopupNames.matchInvoice];

  const [student, setStudent] = useState<IStudentOption | null>(null);
  const [group, setGroup] = useState<IStudentOption | null>(null);
  const [studentGroups, setStudentGroups] = useState<IStudentOption[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  const apollo = useApolloClient();
  const { match, loading: matching } = useInvoiceMatching();

  const close = useCallback(() => {
    setStudent(null);
    setGroup(null);
    setStudentGroups([]);
    hidePopup(PopupNames.matchInvoice);
  }, [hidePopup]);

  // After a student is picked — pull their agreements and surface active
  // groups as picker options. Skip if the caller already supplied options.
  useEffect(() => {
    if (!student) {
      setStudentGroups([]);
      setGroup(null);
      return;
    }
    if (data?.groupOptions?.length) {
      return;
    }
    let cancelled = false;
    setLoadingGroups(true);
    apollo
      .query<StudentDetailQuery, StudentDetailQueryVariables>({
        query: StudentDetailDocument,
        variables: { id: student.value },
        fetchPolicy: 'no-cache',
      })
      .then((result) => {
        if (cancelled) return;
        const agreements = result.data?.student?.agreements || [];
        const seen = new Set<string>();
        const opts: IStudentOption[] = [];
        agreements.forEach((a) => {
          if (!a || a.terminated || !a.group) return;
          if (seen.has(a.group.id)) return;
          seen.add(a.group.id);
          opts.push({ value: a.group.id, label: a.group.name });
        });
        setStudentGroups(opts);
      })
      .finally(() => {
        if (!cancelled) setLoadingGroups(false);
      });
    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [student, apollo, data?.groupOptions]);

  const loadOptions = useCallback(
    async (input: string): Promise<IStudentOption[]> => {
      const result = await apollo.query<
        PaginatedStudentsQuery,
        PaginatedStudentsQueryVariables
      >({
        query: PaginatedStudentsDocument,
        variables: { query: input || data?.buyerEmail || '', first: 20 },
        fetchPolicy: 'no-cache',
      });
      const edges = result.data?.allStudents?.edges || [];
      const opts: IStudentOption[] = [];
      edges.forEach((edge) => {
        const node = edge?.node;
        if (!node || !node.user) return;
        const u = node.user;
        const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
        opts.push({
          value: node.id,
          label: `${fullName || '—'} · ${u.email || ''}`,
        });
      });
      return opts;
    },
    [apollo, data?.buyerEmail],
  );

  const onSubmit = useCallback(async () => {
    if (!data || !student) return;
    const ok = await match(data.invoiceId, student.value, group?.value);
    if (ok) {
      data.onMatched?.();
      close();
    }
  }, [data, student, group, match, close]);

  // Caller-supplied options trump the auto-fetched list — used when the
  // host page already knows the candidate groups (TZ §3.4 advanced flow).
  const groupOptions: IStudentOption[] =
    data?.groupOptions && data.groupOptions.length
      ? data.groupOptions
      : studentGroups;

  if (!data) {
    return null;
  }

  return (
    <PopupContainer
      width="560px"
      onClose={close}
      title={t('pages.fakturownia.matchPopup.title')}
    >
      <Body>
        {data.buyerEmail ? (
          <Hint>
            {t('pages.fakturownia.matchPopup.hint', { email: data.buyerEmail })}
          </Hint>
        ) : null}

        <Field>
          <Label>{t('pages.fakturownia.matchPopup.studentLabel')}</Label>
          <AsyncSelect
            autoFocus
            cacheOptions
            defaultOptions
            value={student}
            onChange={(v) => setStudent((v as IStudentOption) || null)}
            loadOptions={loadOptions}
            placeholder={t('pages.fakturownia.matchPopup.studentPlaceholder')}
          />
        </Field>

        {student && (groupOptions.length > 0 || loadingGroups) ? (
          <Field>
            <Label>{t('pages.fakturownia.matchPopup.groupLabel')}</Label>
            <AsyncSelect
              cacheOptions
              defaultOptions={groupOptions}
              value={group}
              isLoading={loadingGroups}
              onChange={(v) => setGroup((v as IStudentOption) || null)}
              loadOptions={() => Promise.resolve(groupOptions)}
              placeholder={t('pages.fakturownia.matchPopup.groupPlaceholder')}
              isClearable
            />
          </Field>
        ) : null}

        <Actions>
          <Button
            onClick={close}
            variant="outline"
            colors={{ backgroundColor: Colors.White, color: Colors.Blue }}
          >
            {t('pages.fakturownia.matchPopup.cancel')}
          </Button>
          <Button
            onClick={onSubmit}
            variant="primary"
            disabled={!student || matching}
            loading={matching}
            colors={{ backgroundColor: Colors.Blue, color: Colors.White }}
          >
            {t('pages.fakturownia.matchPopup.submit')}
          </Button>
        </Actions>
      </Body>
    </PopupContainer>
  );
});

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 8px 12px;
  border-radius: 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
`;
